import { useEffect } from 'react'
import type { Article } from '../data/articles'
import type { Author } from '../data/authors'

const SITE_NAME = 'FindOnlineColleges'
const SITE_URL = 'https://findonlinecolleges.org'
const PUBLISHER_LOGO =
  'https://cdn.magicpatterns.com/uploads/wkQqc9S9BvV2ZaaGf2SzsH/Nav_Bar.png'
/** Marks every tag this hook owns so cleanup never touches anything else. */
const OWNED = 'data-article-seo'

/** "August 2026" → "2026-08-01". Falls back to undefined if unparseable. */
function toIsoDate(label: string): string | undefined {
  const parsed = new Date(`1 ${label}`)
  if (Number.isNaN(parsed.getTime())) return undefined
  const y = parsed.getFullYear()
  const m = String(parsed.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}-01`
}

function addMeta(attr: 'name' | 'property', key: string, content: string) {
  // Replace any existing tag with the same key so there is exactly one.
  document.head
    .querySelectorAll(`meta[${attr}="${key}"]`)
    .forEach((el) => el.remove())
  const tag = document.createElement('meta')
  tag.setAttribute(attr, key)
  tag.content = content
  tag.setAttribute(OWNED, '')
  document.head.appendChild(tag)
}

function addLink(rel: string, href: string) {
  document.head
    .querySelectorAll(`link[rel="${rel}"]`)
    .forEach((el) => el.remove())
  const link = document.createElement('link')
  link.rel = rel
  link.href = href
  link.setAttribute(OWNED, '')
  document.head.appendChild(link)
}

function addJsonLd(data: object) {
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(data)
  script.setAttribute(OWNED, '')
  document.head.appendChild(script)
}

/**
 * Article-specific search and social metadata, layered on top of the title,
 * description, and base Open Graph tags that usePageMeta already sets:
 *
 * - canonical URL (campaign query strings stripped, so tracked links don't
 *   compete with the clean URL)
 * - robots, keywords, author, image alt, and Twitter large-image fields
 * - article:published_time / modified_time / section / tag / author
 * - JSON-LD Article (with Person author and Organization publisher) and
 *   BreadcrumbList. FAQPage JSON-LD is emitted by ArticleFAQ.
 */
export function useArticleSeo(article: Article | undefined, author?: Author) {
  useEffect(() => {
    if (!article) return

    const canonical = `${SITE_URL}/article/${article.slug}`
    const isoDate = toIsoDate(article.updated)
    const image = article.image
    const imageAlt = article.title
    const tags = [article.targetKeyword, article.category].filter(Boolean)

    addLink('canonical', canonical)
    addMeta(
      'name',
      'robots',
      'index, follow, max-image-preview:large, max-snippet:-1',
    )
    addMeta('name', 'keywords', tags.join(', '))
    if (author) addMeta('name', 'author', author.name)

    // Open Graph overrides + article namespace.
    addMeta('property', 'og:url', canonical)
    addMeta('property', 'og:locale', 'en_US')
    if (image) {
      addMeta('property', 'og:image', image)
      addMeta('property', 'og:image:alt', imageAlt)
      addMeta('name', 'twitter:image', image)
      addMeta('name', 'twitter:image:alt', imageAlt)
    }
    addMeta('property', 'article:section', article.category)
    tags.forEach((tag, i) => {
      // Each article:tag is its own tag; keep keys unique for replacement.
      const meta = document.createElement('meta')
      meta.setAttribute('property', 'article:tag')
      meta.content = tag
      meta.setAttribute(OWNED, '')
      meta.setAttribute('data-index', String(i))
      document.head.appendChild(meta)
    })
    if (isoDate) {
      addMeta('property', 'article:published_time', isoDate)
      addMeta('property', 'article:modified_time', isoDate)
      addMeta('property', 'og:updated_time', isoDate)
    }
    if (author) {
      addMeta('property', 'article:author', `${SITE_URL}/authors#${author.id}`)
    }

    const publisher = {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: PUBLISHER_LOGO },
    }

    addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.metaDescription,
      ...(image ? { image: [image] } : {}),
      ...(isoDate ? { datePublished: isoDate, dateModified: isoDate } : {}),
      articleSection: article.category,
      keywords: tags.join(', '),
      timeRequired: `PT${article.readMinutes}M`,
      inLanguage: 'en-US',
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      author: author
        ? {
            '@type': 'Person',
            name: author.name,
            jobTitle: author.role,
            image: author.image,
            url: `${SITE_URL}/authors#${author.id}`,
          }
        : publisher,
      publisher,
    })

    // ItemList for articles built around a set of options (program types).
    const listSection = article.itemListSectionId
      ? article.sections.find((s) => s.id === article.itemListSectionId)
      : undefined
    if (listSection?.subsections?.length) {
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: listSection.heading,
        numberOfItems: listSection.subsections.length,
        itemListElement: listSection.subsections.map((sub, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: sub.heading,
          description: sub.paragraphs[0],
          url: `${canonical}#${listSection.id}`,
        })),
      })
    }

    addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        // Categories have no page of their own, so the trail is Home → article.
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.title,
          item: canonical,
        },
      ],
    })

    return () => {
      document.head
        .querySelectorAll(`[${OWNED}]`)
        .forEach((el) => el.remove())
    }
  }, [article, author])
}
